import './App.css';
import { PlatformType, Platform } from './constants/platform';
import { Navigate, Route, Routes } from 'react-router';
import NotFound from './components/not-found';
import { Suspense, useEffect, useState } from 'react';
import AuditRoutes from './routes/AuditRoutes';
import InspectRoutes from './routes/InspectRoutes';
import PromptRoutes from './routes/PromptRoute';
import { useFindPermissions } from './utils/helpers/useFindPermissions';
import { logErrorToDB, removedOver5days } from './utils/helpers/logErrorToDb';
import usePermissions from './hooks/usePermissions';

import React from 'react';

if (process.env.REACT_APP_ENV === 'production') {
  for (const method of ['log', 'info', 'debug']) {
    //@ts-ignore
    //eslint-disable-next-line @typescript-eslint/no-empty-function
    console[method as keyof Console] = () => {};
  }
}

const AppRoutes = () => {
  if (!Platform.isAudit() && !Platform.isInspect()) {
    return <Navigate to='/not-found' replace />;
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      {Platform.isAudit() ? <AuditRoutes /> : <InspectRoutes />}
    </Suspense>
  );
};

const App = () => {
  useEffect(() => {
    // Global error handlers
    globalThis.addEventListener('error', (event: ErrorEvent) => {
      const message = event.message;
      const source = event.filename;
      const lineno = event.lineno;
      const colno = event.colno;
      const error = event.error;
      logErrorToDB(error || new Error(message as string), {
        source,
        lineno,
        colno,
      });
      //event.preventDefault()
      return true;
    });

    globalThis.addEventListener(
      'unhandledrejection',
      (event: PromiseRejectionEvent) => {
        const reason =
          event.reason instanceof Error
            ? event.reason
            : new Error(
                typeof event.reason === 'string'
                  ? event.reason
                  : JSON.stringify(event.reason)
              );
        logErrorToDB(reason, { type: 'unhandledrejection' });
      }
    );

    const interval = setInterval(() => {
      if (navigator.onLine) {
        removedOver5days();
      }
    }, 60_000);

    globalThis.addEventListener('online', removedOver5days);

    return () => {
      clearInterval(interval);
      globalThis.removeEventListener('online', removedOver5days);
    };
  }, []);

  const { getPermission } = usePermissions();
  const { canAccessPromptLibrary } = useFindPermissions();
  const [permissionLoaded, setPermissionLoaded] = useState(false);
  useEffect(() => {
    try {
      getPermission().then(() => {
        setPermissionLoaded(true);
      });
    } catch (error) {
      logErrorToDB(error as Error, { type: 'PermissionError' });
    }
  }, [getPermission]);

  const isIframe = globalThis.self !== globalThis.top;
  const isAuditPlatform =
    globalThis?.location?.pathname === '/nonaudit' ||
    globalThis?.location?.pathname === '/audit';
  const platform =
    isIframe || isAuditPlatform ? PlatformType.AUDIT : PlatformType.INSPECT;

  Platform.set(platform);

  if (!Platform.isAudit() && !Platform.isInspect()) {
    return (
      <Routes>
        <Route path='/*' element={<NotFound />} />
      </Routes>
    );
  }

  if (!permissionLoaded) {
    return <div>Authorizing...</div>;
  }
  return (
    <ErrorBoundary>
      <Routes>
        <Route
          path='/prompts/*'
          element={canAccessPromptLibrary ? <PromptRoutes /> : <NotFound />}
        />
        <Route path='/*' element={<AppRoutes />} />
        <Route path='/not-found' element={<NotFound />} />
      </Routes>
    </ErrorBoundary>
  );
};

// Error Boundary Component
interface ErrorBoundaryState {
  hasError: boolean;
}

class ErrorBoundary extends React.Component<
  React.PropsWithChildren<Record<string, unknown>>,
  ErrorBoundaryState
> {
  constructor(props: React.PropsWithChildren<Record<string, unknown>>) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_: Error): ErrorBoundaryState {
    return { hasError: true };
  }

  async componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    await logErrorToDB(error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong.</h1>;
    }
    return this.props.children;
  }
}

export default App;
